import { useList, useApiUrl, useCustomMutation } from '@refinedev/core';
import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { format } from 'date-fns';
import {
  DataTable,
  type Column,
  type SortState,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { RotateCcw, Download } from 'lucide-react';
import { notify } from '@/lib/notify';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { DailyStat } from './types';

export function DailyStatsList() {
  useScrollRestoration();
  const [page, setPage] = usePersistedState('daily-stats:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('daily-stats:sort', { field: 'date', order: 'desc' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const pageSize = 30;
  const apiUrl = useApiUrl();
  const { mutate: refreshMutate, mutation: refreshMutation } = useCustomMutation();

  const filters = useMemo(() => {
    const f = [];
    if (dateFrom) f.push({ field: 'dateFrom', operator: 'gte' as const, value: dateFrom });
    if (dateTo) f.push({ field: 'dateTo', operator: 'lte' as const, value: dateTo });
    return f;
  }, [dateFrom, dateTo]);

  const { result, query } = useList<DailyStat>({
    resource: 'analytics/daily-stats',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters,
    queryOptions: {
      queryKey: ['analytics/daily-stats', page, pageSize, sort?.field, sort?.order, dateFrom, dateTo],
    },
  });

  const data = result?.data ?? [];
  const total = result?.total ?? 0;

  const handleRefresh = () => {
    refreshMutate(
      {
        url: `${apiUrl}/analytics/refresh`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Статистика обновлена');
          void query.refetch();
        },
        onError: () => {
          notify.error('Ошибка обновления статистики');
        },
      },
    );
  };

  const isRefreshing = refreshMutation?.isPending ?? false;

  const handleExport = () => {
    notify.info('Экспорт не реализован');
  };

  const columns: Column<DailyStat>[] = useMemo(() => [
    {
      key: 'date',
      header: 'Дата',
      sortable: true,
      render: (_value, record) => {
        const date = new Date(record.date);
        return date.toLocaleDateString('ru-RU');
      },
    },
    {
      key: 'newUsers',
      header: 'Новые пользователи',
      sortable: true,
      render: (_value, record) => record.newUsers.toString(),
    },
    {
      key: 'activeUsers',
      header: 'Активные пользователи',
      sortable: true,
      render: (_value, record) => record.activeUsers.toString(),
    },
    {
      key: 'newSubscriptions',
      header: 'Новые подписки',
      render: (_value, record) => record.newSubscriptions.toString(),
    },
    {
      // Бэкенд принимает sortBy=revenue (не totalIncome) — используем 'revenue' как sort field
      key: 'revenue',
      header: 'Выручка',
      sortable: true,
      render: (_value, record) => `₽${record.totalIncome.toFixed(2)}`,
    },
    {
      key: 'createdAt',
      header: 'Обновлено',
      render: (_value, record) => {
        const date = new Date(record.createdAt);
        return date.toLocaleString('ru-RU');
      },
    },
  ], []);

  return (
    <div className="space-y-6">
      <FormHeader
        title="Ежедневная статистика"
        sticky
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
            >
              <Download className="h-4 w-4 mr-2" />
              Экспорт
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={query.isLoading || isRefreshing}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Обновить
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-3">
        <div className="w-44">
          <DatePicker
            value={dateFrom ? new Date(dateFrom) : null}
            onChange={(date) => {
              setDateFrom(date ? format(date, 'yyyy-MM-dd') : '');
              setPage(1);
            }}
            placeholder="От"
          />
        </div>
        <span className="text-muted-foreground text-sm">—</span>
        <div className="w-44">
          <DatePicker
            value={dateTo ? new Date(dateTo) : null}
            onChange={(date) => {
              setDateTo(date ? format(date, 'yyyy-MM-dd') : '');
              setPage(1);
            }}
            placeholder="До"
          />
        </div>
        {(dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
              setPage(1);
            }}
          >
            Сбросить
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={query.isLoading}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        sort={sort}
        onSortChange={(newSort) => {
          setSort(newSort);
          setPage(1);
        }}
        emptyMessage="Нет данных статистики"
      />
    </div>
  );
}
